import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import './Lore.css';
import { useTranslation } from 'react-i18next';
import { useMusic } from './hooks/useMusic';
import { MAIN_MENU_MUSIC_PATH } from './config/mainMenuMusic';
import { playSoundEffect } from './hooks/useSoundEffects';
import { API_URL } from './config/apiConfig';
import { useContainerScale } from './hooks/useViewportScale';
import MenuBackButton from './components/MenuBackButton';
import MenuPaintedButton from './components/MenuPaintedButton';
import {
  BATTLE_UI_END_TURN_BUTTON_PATH,
  LORE_NAV_BUTTON_PATH,
  LORE_NAV_BUTTON_SURFACE_FILTER,
  MENU_PAINTED_SURFACE_FILTER,
} from './config/welcomeAssets';
import './StrategyModal.css';
import {
  TutorialMessageBox,
  TutorialReplayButton,
  MENU_TUTORIAL_CATALOG,
  setReplayMenuTutorialRequest,
  useMenuTutorialReplay,
} from './tutorial/index';
import { useTutorialStatus } from './hooks/useTutorialStatus';
import { mergeLoreArticles, parseWikiArticle } from './loreArticles';

function Lore({ user }) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  
  // Viewport scaling for responsive design
  const { 
    scale: viewportScale, 
    offsetX: viewportOffsetX, 
    offsetY: viewportOffsetY,
    baseWidth: containerBaseWidth,
    baseHeight: containerBaseHeight,
    isMobile: isContainerMobile
  } = useContainerScale();
  
  useMusic(MAIN_MENU_MUSIC_PATH);
  const [activeSection, setActiveSection] = useState('factions');
  const [loreEntries, setLoreEntries] = useState({
    factions: [],
    mechanics: [],
    orders: []
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showTutorialLore, setShowTutorialLore] = useState(false);
  const { shouldShowTutorial, markSeenLocally } = useTutorialStatus(user);
  const { isReplayRequest: isReplayingLoreTutorial } = useMenuTutorialReplay('lore', {
    enabled: Boolean(user) && !loading,
    onReplay: () => setShowTutorialLore(true),
  });

  useEffect(() => {
    fetchLoreEntries();
  }, []);

  useEffect(() => {
    if (user && !loading && shouldShowTutorial('lore')) {
      const timer = setTimeout(() => setShowTutorialLore(true), 600);
      return () => clearTimeout(timer);
    }
  }, [user, loading, shouldShowTutorial]);

  const fetchLoreEntries = async () => {
    const emptyLore = {
      factions: [],
      mechanics: [],
      orders: [],
      'order-type': [],
    };

    try {
      setLoading(true);
      setError(null);
      const response = await fetch(`${API_URL}/api/lore-entries`);
      if (!response.ok) {
        throw new Error('Failed to fetch lore entries');
      }
      const data = await response.json();
      // Local Wikipedia-style articles override/add by section + title (not from DB)
      setLoreEntries(mergeLoreArticles(data));
    } catch (err) {
      console.error('Error fetching lore entries:', err);
      // Local wiki articles remain available without the API/database
      setLoreEntries(mergeLoreArticles(emptyLore));
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const getAlphabeticalEntries = () => {
    const currentEntries = loreEntries[activeSection] || [];
    let filteredEntries = currentEntries;
    
    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase();
      filteredEntries = currentEntries.filter(entry => {
        const title = (entry.title || '').toLowerCase();
        
        // Use word boundary regex to avoid partial matches, only search titles
        const searchRegex = new RegExp(`\\b${searchLower.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}`, 'i');
        
        return searchRegex.test(title);
      });
    }
    
    return filteredEntries.sort((a, b) => {
      const titleA = a.title || '';
      const titleB = b.title || '';
      return titleA.localeCompare(titleB);
    });
  };

  const handleEntryClick = (entry) => {
    setSelectedEntry(entry);
  };

  const switchLoreSection = (sectionId) => {
    playSoundEffect('/sounds/click.mp3');
    setActiveSection(sectionId);
    setSelectedEntry(null);
    setSearchTerm('');
  };

  const loreNavButtonProps = {
    className: 'lore-nav-button',
    imagePath: LORE_NAV_BUTTON_PATH,
    surfaceFilter: LORE_NAV_BUTTON_SURFACE_FILTER,
  };

  // Function to parse markdown-style formatting (legacy DB lore entries)
  const parseText = (text) => {
    if (!text) return '';
    
    // Convert **bold** to <strong>bold</strong>
    let parsed = text.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
    
    // Convert *italic* to <em>italic</em>
    parsed = parsed.replace(/\*(.*?)\*/g, '<em>$1</em>');
    
    // Handle both \n and \\n (escaped newlines)
    parsed = parsed.replace(/\\n/g, '<br>');
    parsed = parsed.replace(/\n/g, '<br>');
    
    return parsed;
  };

  const getEntryHtml = (entry) => {
    if (!entry || !entry.text) {
      throw new Error('getEntryHtml: entry.text is required');
    }
    if (entry.format === 'wiki') {
      return parseWikiArticle(entry.text);
    }
    return parseText(entry.text);
  };

  const renderLoreEntry = (entry) => {
    const isWiki = entry.format === 'wiki';
    return (
      <div key={entry.id} className={`lore-entry${isWiki ? ' lore-entry--wiki' : ''}`}>
        <div className="lore-entry-image-column">
          {entry.image && (
            <img 
              src={entry.image} 
              alt={entry.title} 
              className="lore-entry-image"
            />
          )}
        </div>
        <div className="lore-entry-content-column">
          <h3 className="lore-entry-title">{entry.title}</h3>
          <div 
            className={`lore-entry-text${isWiki ? ' lore-wiki-article' : ''}`}
            dangerouslySetInnerHTML={{ __html: getEntryHtml(entry) }}
          />
        </div>
      </div>
    );
  };

  const renderSection = () => {
    if (loading) {
      return <div className="loading">{t('lore.loading')}</div>;
    }

    const entries = loreEntries[activeSection] || [];
    
    if (activeSection === 'tutorials') {
      return (
        <div className="lore-tutorials-main">
          <h3>{t('lore.tutorialsTitle')}</h3>
          <p>{t('lore.tutorialsHint')}</p>
        </div>
      );
    }

    // Prefer local wiki / merged entries over a hard error when the section has content
    if (error && entries.length === 0) {
      return <div className="error">{t('lore.error')} {error}</div>;
    }

    if (entries.length === 0) {
      return <div className="no-entries">{t('lore.noEntries')} {activeSection}.</div>;
    }

    // If an entry is selected, show only that entry
    if (selectedEntry) {
      return (
        <div className="lore-entries">
          {renderLoreEntry(selectedEntry)}
        </div>
      );
    }

    // Show welcome message when no entry is selected
    return (
      <div className="welcome-message">
        <h3>{t('lore.selectEntry')}</h3>
        <p>{t('lore.selectEntryMessage')}</p>
      </div>
    );
  };

  return (
    <div className="lore-viewport-wrapper">
      {/* Full-screen background - outside viewport-container to cover entire screen */}
      <div 
        className="lore-fullscreen-background"
        style={{ backgroundImage: 'url(/images/lore.jpg)' }}
      />
      
      {/* Viewport container - 1920x1080 space that scales to fit screen */}
      <div 
        className={`lore-viewport-container${isContainerMobile ? ' mobile' : ''}`}
        style={{
          width: `${containerBaseWidth}px`,
          height: `${containerBaseHeight}px`,
          transformOrigin: '0 0',
          transform: `translate(${viewportOffsetX}px, ${viewportOffsetY}px) scale(${viewportScale})`,
        }}
      >
        <div className="lore-container">
      
          <div className="lore-header">
            <MenuBackButton to="/welcome" />
            <div className="lore-header-title-block">
              <h1 className="lore-title">{t('lore.title')}</h1>
            </div>
          </div>

      <div className="lore-navigation">
        <MenuPaintedButton
          {...loreNavButtonProps}
          active={activeSection === 'factions'}
          onClick={() => switchLoreSection('factions')}
        >
          {t('lore.factions')}
        </MenuPaintedButton>
        <MenuPaintedButton
          {...loreNavButtonProps}
          active={activeSection === 'mechanics'}
          onClick={() => switchLoreSection('mechanics')}
        >
          {t('lore.mechanics')}
        </MenuPaintedButton>
        <MenuPaintedButton
          {...loreNavButtonProps}
          active={activeSection === 'orders'}
          onClick={() => switchLoreSection('orders')}
        >
          {t('lore.orders')}
        </MenuPaintedButton>
        <MenuPaintedButton
          {...loreNavButtonProps}
          active={activeSection === 'order-type'}
          onClick={() => switchLoreSection('order-type')}
        >
          {t('lore.orderTypes')}
        </MenuPaintedButton>
        <MenuPaintedButton
          {...loreNavButtonProps}
          active={activeSection === 'tutorials'}
          onClick={() => switchLoreSection('tutorials')}
        >
          {t('lore.tutorials')}
        </MenuPaintedButton>
      </div>

      <div className="lore-content">
        <div className="lore-sidebar">
          {activeSection !== 'tutorials' && (
            <div className="lore-search-row">
              <input
                type="text"
                className="lore-search-input"
                placeholder={t('lore.searchPlaceholder')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                autoComplete="off"
              />
            </div>
          )}
          <div className="alphabetical-list">
            {activeSection === 'tutorials' &&
              MENU_TUTORIAL_CATALOG.map((item) => (
                <button
                  key={item.messageKey}
                  type="button"
                  className="alphabetical-entry lore-tutorial-entry"
                  onClick={() => {
                    playSoundEffect('/sounds/click.mp3');
                    setReplayMenuTutorialRequest(item.messageKey);
                    if (item.route !== '/lore') {
                      navigate(item.route);
                    }
                  }}
                >
                  {t(item.labelKey)}
                </button>
              ))}
            {activeSection !== 'tutorials' && loading && (
              <div className="lore-list-placeholder lore-list-placeholder--muted">{t('lore.loading')}</div>
            )}
            {activeSection !== 'tutorials' && !loading && loreEntries[activeSection]?.length > 0 &&
              getAlphabeticalEntries().map((entry) => (
                <button
                  key={entry.id}
                  type="button"
                  className={`alphabetical-entry ${selectedEntry?.id === entry.id ? 'active' : ''}`}
                  onClick={() => {
                    playSoundEffect('/sounds/click.mp3');
                    handleEntryClick(entry);
                  }}
                >
                  {entry.title}
                </button>
              ))}
          </div>
        </div>
        <div className="lore-main-content">
          {renderSection()}
        </div>
      </div>

      <TutorialReplayButton user={user} onReplay={() => setShowTutorialLore(true)} />

      {showTutorialLore && createPortal(
        <TutorialMessageBox
          image="/images/tutorial_wait.png"
          text={t('lore.tutorialText')}
          continueButtonImagePath={BATTLE_UI_END_TURN_BUTTON_PATH}
          continueButtonClassName="strategy-modal-close-btn"
          paintedButtonSurfaceFilter={MENU_PAINTED_SURFACE_FILTER}
          continueLabelKey="lore.tutorialClose"
          onContinue={async () => {
            if (!isReplayingLoreTutorial) {
              const token = localStorage.getItem('token');
              if (token) {
                await fetch(`${API_URL}/tutorial/mark-seen`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                  body: JSON.stringify({ messageKey: 'lore' }),
                });
                markSeenLocally('lore');
              }
            }
            setShowTutorialLore(false);
          }}
          voiceKey="lore.tutorialText"
        />,
        document.body
      )}
        </div>
      </div>
    </div>
  );
}

export default Lore;
